import { useState, useEffect } from 'react';
import axios from 'axios';
import { Heart } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FavoriteButton = ({ listingId, className = '', variant = 'icon', label = 'Favori' }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkFavorite();
  }, [listingId]);

  const checkFavorite = async () => {
    try {
      const { data } = await axios.get(`${API}/favorites/check/${listingId}`, { withCredentials: true });
      setIsFavorite(data.is_favorite);
    } catch (error) {
      // User not logged in or error — silent; button still toggleable
    }
  };

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/remove`, {
          data: { listing_id: listingId },
          withCredentials: true
        });
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites/add`, { listing_id: listingId }, { withCredentials: true });
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to add favorites');
      } else {
        toast.error('Failed to update favorites');
      }
    } finally {
      setLoading(false);
    }
  };

  // Sticky-bar variant: full-width vertical button with label (mobile action bar)
  if (variant === 'sticky') {
    return (
      <button
        onClick={toggleFavorite}
        disabled={loading}
        className={`w-full h-full flex flex-col items-center justify-center gap-0.5 rounded-xl border font-semibold text-xs tracking-wide active:scale-95 transition-transform ${
          isFavorite
            ? 'border-danger bg-danger-soft text-danger'
            : 'border-border bg-white text-text_muted hover:border-danger hover:text-danger'
        } ${className}`}
        data-testid="favorite-button"
      >
        <Heart size={18} weight={isFavorite ? 'fill' : 'regular'} />
        <span>{label}</span>
      </button>
    );
  }

  // Default icon-only variant (original)
  return (
    <button
      onClick={toggleFavorite}
      disabled={loading}
      className={`bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-all ${className}`}
      data-testid="favorite-button"
    >
      <Heart 
        size={20} 
        weight={isFavorite ? 'fill' : 'regular'} 
        className={isFavorite ? 'text-danger' : 'text-text_muted'}
      />
    </button>
  );
};